/**
 * Centralized Consent Manager
 * 
 * Single source of truth for Google Consent Mode updates
 * and GDPR-compliant cookie cleanup on revoke.
 */

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
    __consentModeSet?: boolean;
  }
}

/**
 * Ensure window.gtag is initialized (safe to call multiple times)
 */
export const ensureGtag = (): void => {
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer!.push(args);
    };
  }
};

/**
 * Update Google Consent Mode v2 for both analytics and advertising.
 * This is the ONLY place gtag('consent', 'update', ...) should be called.
 */
export const updateConsentMode = (analytics: boolean, advertising: boolean): void => {
  ensureGtag();

  window.gtag('consent', 'update', {
    analytics_storage: analytics ? 'granted' : 'denied',
    ad_storage: advertising ? 'granted' : 'denied',
    ad_user_data: advertising ? 'granted' : 'denied',
    ad_personalization: advertising ? 'granted' : 'denied',
  });

  window.__consentModeSet = true;
};

/**
 * Delete a cookie by name across multiple domains.
 */
const deleteCookie = (name: string, domains: string[]): void => {
  const paths = ['/', ''];
  for (const domain of domains) {
    for (const path of paths) {
      const domainPart = domain ? `; domain=${domain}` : '';
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path || '/'}${domainPart}; SameSite=Lax`;
      // Also try Secure variant
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path || '/'}${domainPart}; SameSite=None; Secure`;
    }
  }
};

/**
 * Clear all Google advertising cookies (GDPR compliance).
 * Called when user revokes advertising consent.
 */
export const clearAdvertisingCookies = (): void => {
  const adCookies = ['__gads', '__gpi', 'IDE', 'NID', 'ANID', '1P_JAR', 'CONSENT', 'DSID', 'test_cookie'];
  const domains = ['', `.${window.location.hostname}`, '.google.com', '.doubleclick.net'];

  for (const name of adCookies) {
    deleteCookie(name, domains);
  }

  if (import.meta.env.DEV) {
    console.log('[ConsentManager] Cleared advertising cookies');
  }
};

/**
 * Clear all Google Analytics cookies (GDPR compliance).
 * Called when user revokes analytics consent.
 */
export const clearAnalyticsCookies = (): void => {
  const domains = ['', `.${window.location.hostname}`];

  // Clear known GA cookies
  for (const name of ['_ga', '_gid', '_gat']) {
    deleteCookie(name, domains);
  }

  // Clear _ga_* cookies (measurement ID specific)
  const allCookies = document.cookie.split(';');
  for (const cookie of allCookies) {
    const cookieName = cookie.trim().split('=')[0];
    if (cookieName.startsWith('_ga_')) {
      deleteCookie(cookieName, domains);
    }
  }

  if (import.meta.env.DEV) {
    console.log('[ConsentManager] Cleared analytics cookies');
  }
};
